import { useCallback, useEffect, useState } from 'react';
import { LuBell, LuCreditCard, LuHistory, LuInfo, LuPlus, LuRefreshCw, LuWallet } from 'react-icons/lu';
import API from '../../api/axios';
import AppLayout from '../../components/layout/AppLayout';
import { Badge, Button, Card, EmptyState, Loader, Page, PageHeader, Tabs } from '../../components/ui';
import PaiementAbonnement, { fraisEnLigne } from '../../components/abonnement/PaiementAbonnement';
import { fmtF } from '../../theme';
import '../../components/abonnement/Abonnement.css';

export const ETATS = {
  actif:          { label: 'Actif',            tone: 'success' },
  expire_bientot: { label: 'Expire bientôt',   tone: 'warn' },
  grace:          { label: 'Expiré (grâce)',    tone: 'danger' },
  lecture_seule:  { label: 'Lecture seule',    tone: 'danger' },
  suspendu:       { label: 'Suspendu',         tone: 'danger' },
  aucun:          { label: 'Sans abonnement',  tone: 'neutral' },
};
export const SOURCES = { manuel: 'Paiement enregistré', en_ligne: 'Paiement en ligne', solde: 'Solde prépayé' };
const dateFr = d => (d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const msgErreur = e => e?.response?.data?.message?.replace(/^❌\s*/, '') || 'Erreur réseau';

export default function AbonnementProprietaire() {
  const [donnees, setDonnees] = useState(null);
  const [plans, setPlans] = useState([]);
  const [durees, setDurees] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [payer, setPayer] = useState(null);
  const [recharge, setRecharge] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [historique, setHistorique] = useState('paiements');
  const [toast, setToast] = useState('');
  const notifier = useCallback(t => { setToast(t); setTimeout(() => setToast(''), 4000); }, []);

  const charger = useCallback(async () => {
    try {
      const [a, p, d, n] = await Promise.all([
        API.get('/abonnements/mes-abonnements'),
        API.get('/abonnements/plans'),
        API.get('/abonnements/durees'),
        API.get('/abonnements/notifications'),
      ]);
      setDonnees(a.data);
      setPlans(p.data.plans || []);
      setDurees(d.data.durees || []);
      setNotifications(n.data.notifications || []);
    } catch (e) { notifier('❌ ' + msgErreur(e)); }
  }, [notifier]);

  useEffect(() => { charger(); }, [charger]);

  // Ouverture directe de la fenêtre de paiement depuis un lien (?payer=<id>)
  useEffect(() => {
    if (!donnees) return;
    const id = Number(new URLSearchParams(window.location.search).get('payer'));
    const a = id && donnees.abonnements.find(x => x.etablissement_id === id);
    if (a) setPayer(a);
  }, [donnees]);

  const marquerLues = async () => {
    try { await API.patch('/abonnements/notifications/lu'); setNotifications(ns => ns.map(n => ({ ...n, lu: true }))); } catch (_) { /* ignore */ }
  };

  const regler = async (a, champs) => {
    try {
      await API.put(`/abonnements/${a.etablissement_id}/renouvellement`, champs);
      setDonnees(d => ({ ...d, abonnements: d.abonnements.map(x => (x.etablissement_id === a.etablissement_id ? { ...x, ...champs } : x)) }));
      notifier('✅ Préférences enregistrées');
    } catch (e) { notifier('❌ ' + msgErreur(e)); }
  };

  const recharger = async () => {
    const montant = Number(recharge);
    if (!(montant >= 100)) return notifier('❌ Montant minimum : 100 FCFA');
    setEnvoi(true);
    try {
      const r = await API.post('/abonnements/recharger', { montant });
      window.location.href = r.data.payment_url;
    } catch (e) { notifier('❌ ' + msgErreur(e)); setEnvoi(false); }
  };

  if (!donnees) return <AppLayout role="proprietaire"><Loader fullScreen={false} /></AppLayout>;
  const nonLues = notifications.filter(n => !n.lu).length;

  return (
    <AppLayout role="proprietaire">
      <Page>
        {toast && <div role="status" className={`ab-toast ${toast.startsWith('❌') ? 'is-err' : ''}`}>{toast.replace(/^(✅|❌)\s*/, '')}</div>}
        <PageHeader title="Abonnement" subtitle="Vos établissements, votre solde prépayé et vos paiements" />

        {(donnees.simulation || donnees.sandbox) && (
          <div className="ab-bandeau is-alerte" style={{ borderRadius: 12, marginBottom: 16 }}>
            <LuInfo aria-hidden className="ab-bandeau-icone" />
            <span className="ab-bandeau-texte">
              {donnees.simulation
                ? "Mode test : les paiements en ligne sont simulés, aucun argent n'est débité."
                : "Mode test CinetPay (sandbox) : la monnaie est virtuelle, aucun argent réel n'est débité."}
            </span>
          </div>
        )}

        {/* ── Solde prépayé ── */}
        <Card style={{ marginBottom: 20 }}>
          <div className="ab-solde">
            <div>
              <div className="ab-solde-libelle">Solde prépayé</div>
              <div className="ab-solde-montant">{fmtF(donnees.solde)}</div>
              <div className="ab-sub" style={{ maxWidth: '60ch', marginTop: 4 }}>
                Avec le renouvellement automatique activé, l'abonnement est prélevé sur ce solde la veille de l'échéance, sans aucune action de votre part.
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 'min(280px, 100%)' }}>
              <div className="ab-actions">
                {[10000, 25000, 50000].map(m => (
                  <Button key={m} variant="secondary" style={{ minHeight: 34, padding: '0 10px', fontSize: 13 }} onClick={() => setRecharge(String(m))}>{fmtF(m)}</Button>
                ))}
              </div>
              <div className="ab-actions" style={{ flexWrap: 'nowrap' }}>
                <input className="ab-input" type="number" min="100" step="5" inputMode="numeric" placeholder="Montant (FCFA)"
                  value={recharge} onChange={e => setRecharge(e.target.value)} aria-label="Montant de la recharge" />
                <Button icon={LuPlus} onClick={recharger} disabled={envoi || !donnees.paiement_en_ligne} style={{ flexShrink: 0 }}>Recharger</Button>
              </div>
              {!donnees.paiement_en_ligne && <span className="ab-sub">Paiement en ligne bientôt disponible.</span>}
              {donnees.paiement_en_ligne && donnees.frais_pourcent > 0 && (() => {
                const m = Math.ceil(Number(recharge) / 5) * 5;
                if (!(m >= 100)) return <span className="ab-sub">Frais de paiement en ligne : {donnees.frais_pourcent} % du montant.</span>;
                const fr = fraisEnLigne(m, donnees.frais_pourcent);
                return (
                  <span className="ab-sub">
                    Crédité : <span className="ab-num">{fmtF(m)}</span> · frais ({donnees.frais_pourcent} %) : <span className="ab-num">{fmtF(fr)}</span> · <strong>à payer : <span className="ab-num">{fmtF(m + fr)}</span></strong>
                  </span>
                );
              })()}
            </div>
          </div>
        </Card>

        {/* ── Établissements ── */}
        {donnees.abonnements.length === 0 ? (
          <Card><EmptyState icon={LuCreditCard} title="Aucun établissement" text="Vos établissements et leurs abonnements apparaîtront ici." /></Card>
        ) : (
          <div className="ab-etabs" style={{ marginBottom: 20 }}>
            {donnees.abonnements.map(a => {
              const e = ETATS[a.etat] || ETATS.aucun;
              const prochain = plans.find(p => p.id === a.plan_id);
              const d = durees.find(x => x.mois === a.duree_renouvellement) || durees[0];
              const prixAuto = prochain && d ? Math.ceil((prochain.prix_mensuel * d.mois * (1 - d.remise_pct / 100)) / 5) * 5 : null;
              return (
                <article key={a.etablissement_id} className={`ab-etab is-${a.etat}`}>
                  <div className="ab-etab-tete">
                    <div style={{ minWidth: 0 }}>
                      <div className="ab-etab-nom">{a.etablissement_nom}</div>
                      <div className="ab-sub">Plan {a.plan_nom || '—'}{a.prix_mensuel ? ` · ${fmtF(a.prix_mensuel)} / mois` : ''}</div>
                    </div>
                    <Badge tone={e.tone}>{e.label}</Badge>
                  </div>

                  <div className="ab-lignes">
                    <div className="ab-ligne"><span>Échéance</span><strong className="ab-num">{dateFr(a.date_fin)}</strong></div>
                    <div className="ab-ligne">
                      <span>{a.etat === 'grace' ? 'Lecture seule dans' : 'Temps restant'}</span>
                      <strong className="ab-num">
                        {a.etat === 'grace' ? `${a.jours_grace_restants} j`
                          : a.jours_restants > 0 ? `${a.jours_restants} jour${a.jours_restants > 1 ? 's' : ''}` : 'Expiré'}
                      </strong>
                    </div>
                  </div>

                  {a.id && a.etat !== 'suspendu' && (
                    <div className="ab-auto">
                      <label className="ab-switch">
                        <input type="checkbox" checked={a.renouvellement_auto} onChange={ev => regler(a, { renouvellement_auto: ev.target.checked })} />
                        <span className="ab-switch-piste" aria-hidden />
                        Renouvellement automatique
                      </label>
                      {a.renouvellement_auto && (
                        <>
                          <span className="ab-sub">tous les</span>
                          <select className="ab-input" value={a.duree_renouvellement} onChange={ev => regler(a, { duree_renouvellement: Number(ev.target.value) })} aria-label="Durée du renouvellement">
                            {durees.map(x => <option key={x.mois} value={x.mois}>{x.mois} mois{x.remise_pct > 0 ? ` (−${x.remise_pct} %)` : ''}</option>)}
                          </select>
                          {prixAuto !== null && (
                            <span className="ab-sub" style={{ flexBasis: '100%' }}>
                              {fmtF(prixAuto)} prélevés sur le solde la veille de l'échéance
                              {donnees.solde < prixAuto && <strong style={{ color: '#B45309' }}> · solde insuffisant</strong>}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {a.etat === 'suspendu'
                    ? <p className="ab-sub" style={{ margin: 0 }}>Abonnement suspendu par StockPro. Contactez-nous pour le réactiver.</p>
                    : <Button icon={LuRefreshCw} onClick={() => setPayer(a)} disabled={!plans.length}>
                        {['grace', 'lecture_seule'].includes(a.etat) ? 'Réactiver maintenant' : 'Payer / prolonger'}
                      </Button>}
                </article>
              );
            })}
          </div>
        )}

        {/* ── Notifications ── */}
        <Card
          title={<span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><LuBell aria-hidden /> Notifications {nonLues > 0 && <Badge tone="danger">{nonLues}</Badge>}</span>}
          action={nonLues > 0 && <Button variant="secondary" onClick={marquerLues} style={{ minHeight: 34 }}>Tout marquer comme lu</Button>}
          style={{ marginBottom: 20 }}
        >
          {notifications.length === 0 ? <p className="ab-sub" style={{ margin: 0 }}>Aucune notification.</p> : (
            <div className="ab-notifs">
              {notifications.slice(0, 8).map(n => (
                <div key={n.id} className={`ab-notif is-${n.niveau} ${n.lu ? '' : 'is-nonlu'}`}>
                  <span className="ab-notif-point" aria-hidden />
                  <div style={{ minWidth: 0 }}>
                    <div>{n.titre}</div>
                    {n.message && <div className="ab-sub" style={{ fontWeight: 400 }}>{n.message}</div>}
                    <div className="ab-sub" style={{ fontSize: 12, fontWeight: 400 }}>{new Date(n.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ── Historique ── */}
        <Card title={<span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><LuHistory aria-hidden /> Historique</span>}>
          <Tabs
            tabs={[{ id: 'paiements', label: 'Paiements', icon: LuCreditCard }, { id: 'solde', label: 'Mouvements du solde', icon: LuWallet }]}
            value={historique} onChange={setHistorique}
          />
          <div className="ab-table-wrap">
            {historique === 'paiements' ? (
              donnees.paiements.length === 0 ? <p className="ab-sub">Aucun paiement.</p> : (
                <table className="ab-table">
                  <thead><tr><th>Date</th><th>Établissement</th><th>Période</th><th>Moyen</th><th style={{ textAlign: 'right' }}>Montant</th></tr></thead>
                  <tbody>
                    {donnees.paiements.map(p => (
                      <tr key={p.id}>
                        <td className="ab-num">{dateFr(p.created_at)}</td>
                        <td>{p.etablissement_nom}</td>
                        <td>{p.mois_payes} mois{p.plan_nom ? ` · ${p.plan_nom}` : ''}</td>
                        <td>{SOURCES[p.source] || p.mode_paiement}</td>
                        <td className="ab-num" style={{ textAlign: 'right', fontWeight: 700 }}>{fmtF(p.montant)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : (
              donnees.mouvements_solde.length === 0 ? <p className="ab-sub">Aucun mouvement.</p> : (
                <table className="ab-table">
                  <thead><tr><th>Date</th><th>Opération</th><th style={{ textAlign: 'right' }}>Montant</th><th style={{ textAlign: 'right' }}>Solde</th></tr></thead>
                  <tbody>
                    {donnees.mouvements_solde.map(m => (
                      <tr key={m.id}>
                        <td className="ab-num">{dateFr(m.created_at)}</td>
                        <td>{m.motif}</td>
                        <td className="ab-num" style={{ textAlign: 'right', fontWeight: 700, color: m.type === 'credit' ? '#047857' : '#B91C1C' }}>
                          {m.type === 'credit' ? '+' : '−'}{fmtF(m.montant)}
                        </td>
                        <td className="ab-num" style={{ textAlign: 'right' }}>{fmtF(m.solde_apres)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </Card>

        <p className="ab-sub" style={{ marginTop: 14, display: 'flex', gap: 6, alignItems: 'flex-start', maxWidth: '75ch' }}>
          <LuInfo aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
          Sans paiement à l'échéance, StockPro reste utilisable {donnees.grace_jours} jours, puis passe en lecture seule : vous pouvez consulter vos données, mais plus enregistrer de ventes ni modifier le stock jusqu'au paiement.
        </p>

        {payer && (
          <PaiementAbonnement
            abonnement={payer} plans={plans} durees={durees} solde={donnees.solde}
            paiementEnLigne={donnees.paiement_en_ligne} fraisPourcent={donnees.frais_pourcent || 0} notifier={notifier}
            onClose={() => setPayer(null)} onPaye={() => { setPayer(null); charger(); }}
          />
        )}
      </Page>
    </AppLayout>
  );
}
